import styled from '@emotion/styled';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { formatDateAndTime } from '@racemap/utilities/formatting';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import { type FC, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { IconChartLine, IconExternal } from '../Icon';
import { eventHasAssociates } from '../utils/eventHasAssociates';

interface Props {
  eventIds: Immutable<Set<string>>;
  trackId: string | undefined;
  associatedUsers: Immutable<Set<string>>;
}

const apiClient = RacemapAPIClient.fromWindowLocation();

export const TrackerEventsTable: FC<Props> = ({ eventIds, trackId, associatedUsers }) => {
  const [events, setEvents] = useState<Array<RacemapEvent>>([]);

  const loadEvents = async () => {
    const newEvents: Array<RacemapEvent> = [];
    for (const eventId of eventIds) {
      let newEvent: RacemapEvent | undefined;
      const existingTracker = events.find((event) => event.id === eventId);
      if (existingTracker) {
        newEvent = existingTracker;
      } else {
        newEvent = await apiClient.getEvent(eventId);
      }
      if (await eventHasAssociates(newEvent, associatedUsers)) {
        newEvents.push(newEvent);
      }
    }

    newEvents.sort((a, b) => Date.parse(b.startTime) - Date.parse(a.startTime));
    setEvents(newEvents);
  };

  useEffect(() => {
    loadEvents();
  }, [eventIds]);

  return (
    <Table striped bordered size="sm" hover>
      <>
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Startet at</th>
            <th>Endet at</th>
            <th />
          </tr>
        </thead>
        {buildTableBody(events, trackId)}
      </>
    </Table>
  );
};

const buildTableBody = (events: Array<RacemapEvent>, trackId: string | undefined) => {
  return (
    <tbody>
      {events.map((event, i) => (
        <tr key={event.id}>
          <th>{i}</th>
          <th>{event.name || event.id}</th>
          <th>{formatDateAndTime(event.startTime)}</th>
          <th>{formatDateAndTime(event.endTime)}</th>
          <th>
            <IconGroup>
              <Link to={`/admin/events/${event.id}`}>
                <IconExternal />
              </Link>
              {trackId && (
                <Link
                  to={`/admin/track/${trackId}?startDate=${event.startTime}&endDate=${event.endTime}&outageDuration=${event.playerOptions.markerTimeout}`}
                >
                  <IconChartLine />
                </Link>
              )}
            </IconGroup>
          </th>
        </tr>
      ))}
    </tbody>
  );
};

const IconGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
`;
